import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useLingui } from "@lingui/react/macro";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ConfirmDeleteDialog } from "@/components/common/ConfirmDeleteDialog";
import { useUoms, usePackContainers } from "@/hooks/useUomReference";
import {
  useMaterialConversions,
  useCreateMaterialConversion,
  useDeleteMaterialConversion,
  useUpdateMaterialConversion,
} from "@/hooks/useMaterialConversions";
import { getApiErrorFields, getApiErrorMessage, isConflictError } from "@/lib/api/api";
import { formatNumber, isPriceSuspicious } from "@/lib/materials/conversion-calc";
import {
  CONVERTER_COLUMNS,
  converterPayload,
  converterProblem,
  converterStateFromConversion,
  emptyConverterState,
  MAX_YIELD_PCT,
  usableCostPerBase,
  type ConverterState,
} from "@/lib/materials/conversion-grid";
import type { PackContainerOption } from "@/lib/materials/pack-size-parser";
import type { IngredientConversion, Uom } from "@/types/material-conversion";
import { PackSizeConverter } from "../../pack-size/PackSizeConverter";

interface UomPackSizeTabProps {
  materialId: number;
  canEdit: boolean;
}

/** Sentinel selection meaning "adding a brand-new supplier item". */
const NEW_ITEM = "new" as const;

export function UomPackSizeTab({ materialId, canEdit }: UomPackSizeTabProps) {
  const { t } = useLingui();
  const { data: uoms, isLoading: uomsLoading } = useUoms();
  const { data: containers, isLoading: containersLoading } = usePackContainers();
  const { data: conversions, isLoading: conversionsLoading } = useMaterialConversions(materialId);
  const [selectedId, setSelectedId] = useState<number | typeof NEW_ITEM | null>(null);

  if (uomsLoading || containersLoading || conversionsLoading || !uoms || !containers) {
    return (
      <div className="flex flex-col gap-4 px-6">
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  const items = conversions ?? [];
  // Nothing saved yet: go straight to adding one rather than showing an empty list.
  const selection = selectedId ?? items[0]?.id ?? NEW_ITEM;
  const selected = selection === NEW_ITEM ? null : (items.find((c) => c.id === selection) ?? null);

  return (
    <div className="flex flex-col gap-5 px-6">
      {items.length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => setSelectedId(item.id)}
              className={`flex flex-col items-start gap-0.5 rounded-xl border px-3 py-2 text-left transition-colors ${
                selection === item.id
                  ? "border-(--color-action) bg-(--color-surface-content)"
                  : "border-(--color-border) bg-(--color-surface-muted)"
              }`}
            >
              <span className="text-sm font-semibold text-(--color-text-primary)">
                {item.supplier?.name ?? item.supplierCode ?? t`Unnamed supplier item`}
              </span>
              <span className="text-xs text-(--color-text-secondary)">{item.packSizeText ?? "—"}</span>
            </button>
          ))}
          {canEdit && items.length > 0 && (
            <Button type="button" variant="outline" size="sm" onClick={() => setSelectedId(NEW_ITEM)} className="gap-1.5">
              <Plus className="size-4" />
              {t`Add supplier item`}
            </Button>
          )}
        </div>
      )}

      <SupplierItemEditor
        // Remounts on a switch between items, so the form starts from the
        // selected item without a re-seeding effect.
        key={selection}
        materialId={materialId}
        conversion={selected}
        uoms={uoms}
        containers={containers}
        canEdit={canEdit}
        onSaved={(id) => setSelectedId(id)}
        onDeleted={() => setSelectedId(null)}
      />
    </div>
  );
}

function SupplierItemEditor({
  materialId,
  conversion,
  uoms,
  containers,
  canEdit,
  onSaved,
  onDeleted,
}: {
  materialId: number;
  conversion: IngredientConversion | null;
  uoms: Uom[];
  containers: PackContainerOption[];
  canEdit: boolean;
  onSaved: (id: number) => void;
  onDeleted: () => void;
}) {
  const { t } = useLingui();
  const initial = () => (conversion ? converterStateFromConversion(conversion, uoms, containers) : emptyConverterState());
  const [state, setState] = useState<ConverterState>(initial);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({});
  const [priceConfirmed, setPriceConfirmed] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const createConversion = useCreateMaterialConversion(materialId);
  const updateConversion = useUpdateMaterialConversion(materialId);
  const deleteConversion = useDeleteMaterialConversion(materialId);
  const isSaving = createConversion.isPending || updateConversion.isPending;

  const payload = converterPayload(state, uoms);
  const problem = converterProblem(state);

  // A price far from the one on record is usually a pack size or case price
  // typed wrong, so it is checked against the invoice before it is saved.
  const priceChange = (() => {
    if (!conversion) return null;
    for (const column of CONVERTER_COLUMNS) {
      const before = usableCostPerBase(conversion, column);
      const after = usableCostPerBase(
        {
          casePrice: payload.casePrice ?? null,
          eachPerCase: payload.eachPerCase ?? null,
          eachYieldPct: payload.eachYieldPct ?? null,
          weightPerCaseG: payload.weightPerCaseG ?? null,
          weightYieldPct: payload.weightYieldPct ?? null,
          liquidPerCaseMl: payload.liquidPerCaseMl ?? null,
          liquidYieldPct: payload.liquidYieldPct ?? null,
        },
        column,
      );
      // Every column is checked: a case whose weight is unchanged can still
      // have its count or volume typed wrong.
      if (before != null && after != null && isPriceSuspicious(after, before)) return after / before;
    }
    return null;
  })();
  const needsPriceCheck = priceChange != null && !priceConfirmed;

  function change(next: ConverterState) {
    setState(next);
    setPriceConfirmed(false);
  }

  async function save() {
    setFieldErrors({});
    try {
      if (conversion) {
        await updateConversion.mutateAsync({ conversionId: conversion.id, data: payload });
        toast.success(t`Supplier item updated`);
      } else {
        const created = await createConversion.mutateAsync(payload);
        toast.success(t`Supplier item added`);
        onSaved(created.id);
      }
    } catch (error) {
      setFieldErrors(getApiErrorFields(error));
      toast.error(
        isConflictError(error)
          ? t`This supplier already has a pack size on this ingredient`
          : getApiErrorMessage(error, t`Failed to save`),
      );
    }
  }

  async function remove() {
    if (!conversion) return;
    try {
      await deleteConversion.mutateAsync(conversion.id);
      toast.success(t`Supplier item removed`);
      setDeleteOpen(false);
      onDeleted();
    } catch (error) {
      toast.error(getApiErrorMessage(error, t`Failed to remove`));
    }
  }

  return (
    <div className="flex flex-col gap-5">
      {canEdit && (
        <div className="flex flex-wrap items-center justify-end gap-3">
          {conversion && (
            <Button type="button" variant="ghost" onClick={() => setDeleteOpen(true)} className="mr-auto gap-1.5 text-(--color-danger-text)">
              <Trash2 className="size-4" />
              {t`Remove supplier item`}
            </Button>
          )}
          <Button type="button" variant="outline" size="lg" onClick={() => change(initial())} disabled={isSaving}>
            {t`Cancel`}
          </Button>
          <Button type="button" size="lg" onClick={save} disabled={isSaving || problem !== null || needsPriceCheck}>
            {isSaving ? t`Saving…` : t`Save`}
          </Button>
        </div>
      )}

      {canEdit && problem === "EMPTY" && (state.packText.trim() !== "" || state.casePrice.trim() !== "") && (
        <p className="text-sm text-(--color-text-secondary)">{t`Say what a case holds — a count, a weight or a volume — to save.`}</p>
      )}
      {problem === "YIELD" && <p className="text-sm text-(--color-danger-text)">{t`Yield has to be between 1% and ${MAX_YIELD_PCT}%.`}</p>}
      {problem === "PRICE" && <p className="text-sm text-(--color-danger-text)">{t`Case price has to be a number.`}</p>}

      {needsPriceCheck && priceChange != null && (
        <div className="flex flex-wrap items-center gap-3 rounded-2xl bg-(--color-warning-bg) p-4 text-(--color-warning-text)">
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <span className="font-bold">
              {priceChange > 1
                ? t`This is about ${formatNumber(priceChange, 1)}× the cost on record.`
                : t`This is about ${formatNumber(1 / priceChange, 1)}× less than the cost on record.`}
            </span>
            <span className="text-sm">{t`Check the pack size and case price against the invoice.`}</span>
          </div>
          <Button type="button" variant="outline" onClick={() => setPriceConfirmed(true)}>
            {t`Price is correct — keep it`}
          </Button>
        </div>
      )}

      <PackSizeConverter
        value={state}
        onChange={change}
        uoms={uoms}
        containers={containers}
        readOnly={!canEdit}
        fieldErrors={fieldErrors}
      />

      <ConfirmDeleteDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title={t`Remove this supplier item?`}
        description={t`Its pack size, yields and recipe units are removed from this ingredient. Recipes stop costing from it.`}
        confirmLabel={t`Remove`}
        onConfirm={remove}
        isPending={deleteConversion.isPending}
      />
    </div>
  );
}
